import {
  EMPTY_REASON_BY_CODE,
  normalizeActionRowStatus,
} from './reportActionResultContract';

const normalizeValue = (value) => {
  if (value === undefined || value === null) return '';
  return String(value).trim();
};

const getRowField = (row, field) => {
  if (!row || !field) return undefined;
  const key = String(field);
  if (Object.prototype.hasOwnProperty.call(row, key)) return row[key];
  if (!key.includes('.')) return row[key];
  return key
    .split('.')
    .map((segment) => segment.trim())
    .filter(Boolean)
    .reduce((acc, segment) => (acc == null ? acc : acc[segment]), row);
};

const firstValue = (row, fields = []) => {
  for (const field of fields) {
    const value = getRowField(row, field);
    if (normalizeValue(value)) return value;
  }
  return '';
};

const normalizeActionStatus = (status) => {
  return normalizeActionRowStatus(status, 'queued');
};

const formatSkippedReason = (entry = {}) => {
  if (Array.isArray(entry.missing) && entry.missing.length) {
    return `Missing required fields: ${entry.missing.join(', ')}.`;
  }
  if (Array.isArray(entry.missingFields) && entry.missingFields.length) {
    return `Missing required fields: ${entry.missingFields.join(', ')}.`;
  }
  if (EMPTY_REASON_BY_CODE[entry.reason]) return EMPTY_REASON_BY_CODE[entry.reason];
  return entry.reason || '';
};

const resolveReportActionRowId = (row, index = 0) =>
  normalizeValue(firstValue(row, ['__rowId', 'id', 'rowId', 'referenceId', 'selectionKey', '__orderItemId', 'bac'])) ||
  `report-action-row-${index}`;

const buildReportActionDisplayRow = (row, index, overrides = {}) => {
  const hasSourceRow = row && Object.keys(row).length > 0;
  return {
    id: normalizeValue(
      hasSourceRow
        ? resolveReportActionRowId(row, index)
        : (overrides.id || overrides.referenceId || overrides.selectionKey || overrides.orderItemId || resolveReportActionRowId(row, index))
    ),
    index,
    status: normalizeActionStatus(overrides.status),
    reason: normalizeValue(overrides.reason),
    bac: normalizeValue(overrides.bac || firstValue(row, ['bac', 'Dealer BAC', 'BAC'])),
    programName: normalizeValue(
      overrides.programName || firstValue(row, ['programName', 'programCode', 'program', '__reportProgram', '__dpeProgram'])
    ),
    productLabel: normalizeValue(
      overrides.productLabel ||
        firstValue(row, [
          'productBrandCode',
          'productBrandCd',
          'productDescription',
          'OrderItem DI Brand Name',
          'OemProductCodePopcorn',
          'serviceItemName',
        ])
    ),
    referenceId: normalizeValue(overrides.referenceId || firstValue(row, ['referenceId', 'referenceID'])),
    orderItemId: normalizeValue(overrides.orderItemId || firstValue(row, ['__orderItemId', 'orderItemId'])),
    selectionKey: normalizeValue(overrides.selectionKey || firstValue(row, ['selectionKey'])),
    diStatus: normalizeValue(overrides.diStatus || firstValue(row, ['di.status', 'diStatus'])),
    diSelectionDate: normalizeValue(
      overrides.diSelectionDate || firstValue(row, ['di.selectionDate', 'diSelectionDate'])
    ),
    diEffectiveDate: normalizeValue(
      overrides.diEffectiveDate || firstValue(row, ['di.effectiveDate', 'diEffectiveDate'])
    ),
  };
};

const findSourceRow = (sourceRows, result, fallbackIndex) => {
  const candidates = [
    result?.id,
    result?.referenceId,
    result?.selectionKey,
    result?.orderItemId,
  ].map(normalizeValue).filter(Boolean);
  if (candidates.length) {
    const matchedRow = sourceRows.find((row, index) => {
      const ids = [
        resolveReportActionRowId(row, index),
        firstValue(row, ['referenceId', 'referenceID']),
        firstValue(row, ['selectionKey']),
        firstValue(row, ['__orderItemId', 'orderItemId']),
        firstValue(row, ['bac', 'Dealer BAC', 'BAC']),
      ].map(normalizeValue);
      return candidates.some((candidate) => ids.includes(candidate));
    });
    if (matchedRow) return matchedRow;
  }
  if (Number.isInteger(result?.index) && sourceRows[result.index]) return sourceRows[result.index];
  return sourceRows[fallbackIndex] || {};
};

const buildPendingReportActionRows = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((row, index) =>
    buildReportActionDisplayRow(row, index, { status: 'sending', reason: 'Sending...' })
  );

const buildReportActionRowsFromResult = ({ sourceRows = [], result = {} }) => {
  const rowResults = Array.isArray(result?.rowResults) ? result.rowResults : [];
  if (rowResults.length) {
    return rowResults.map((entry, index) => {
      const sourceRow = findSourceRow(sourceRows, entry, index);
      return buildReportActionDisplayRow(sourceRow, Number.isInteger(entry.index) ? entry.index : index, entry);
    });
  }

  const status = result?.ok === false ? 'failed' : 'success';
  return buildPendingReportActionRows(sourceRows).map((entry) => ({
    ...entry,
    status,
    reason: status === 'success' ? '' : normalizeValue(result?.reason || 'Report action failed.'),
  }));
};

const buildReportActionRowsFromError = ({ sourceRows = [], error, fallbackMessage = 'Report action failed.' }) => {
  const details = error?.response?.data?.details;
  const resultRows = Array.isArray(details?.rowResults) ? details.rowResults : [];
  if (resultRows.length) {
    return buildReportActionRowsFromResult({ sourceRows, result: { rowResults: resultRows } });
  }
  const skippedRows = Array.isArray(details?.skipped) ? details.skipped : [];
  if (skippedRows.length) {
    return buildReportActionRowsFromResult({
      sourceRows,
      result: {
        rowResults: skippedRows.map((entry) => ({ ...entry, status: 'skipped', reason: formatSkippedReason(entry) })),
      },
    });
  }
  const message =
    normalizeValue(error?.response?.data?.message) ||
    normalizeValue(error?.response?.data?.reason) ||
    normalizeValue(error?.message) ||
    fallbackMessage;
  return buildPendingReportActionRows(sourceRows).map((entry) => ({
    ...entry,
    status: 'failed',
    reason: message,
  }));
};

export {
  buildPendingReportActionRows,
  buildReportActionRowsFromError,
  buildReportActionRowsFromResult,
  normalizeActionStatus,
  resolveReportActionRowId,
};
